import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useEffect, useState } from 'react'
import { Avatar } from '../../components/avatar/Avatar'
import { VendorImage } from '../../components/avatar/vendorImage'
import { Button } from '../../components/button'
import { Status } from '../../components/cards/statusTag'
import { TabNav } from '../../components/nav/tabNav'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { moveElement } from '../../helpers'
import Layout from '../../layout'
import {
  useGetCreatorCurationQuery,
  useUpdateCreatorSectionOrderMutation,
} from '../../redux-toolkits/vendors/vendors.slice'
import {
  CuratedCreator,
  CreatorSection,
} from '../../redux-toolkits/vendors/vendors.types'

const sectionTabs: { id: CreatorSection; name: string }[] = [
  { id: 'FOOD', name: 'Food' },
  { id: 'LIFESTYLE', name: 'Lifestyle' },
  // { id: 'EVENT', name: 'Events' },
]

export const CreatorCurationPage = () => {
  const [section, setSection] = useState<CreatorSection>('FOOD')
  const [creators, setCreators] = useState<CuratedCreator[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const { data, isFetching } = useGetCreatorCurationQuery(section, {
    refetchOnMountOrArgChange: true,
  })
  const [updateCreatorOrder, { isLoading: isSaving }] =
    useUpdateCreatorSectionOrderMutation()

  useEffect(() => {
    if (data?.data.creators) {
      setCreators(data.data.creators)
      setHasUnsavedChanges(false)
    }
  }, [data])

  const changeSection = (nextSection: CreatorSection) => {
    if (
      hasUnsavedChanges &&
      !window.confirm('Discard the unsaved creator order for this section?')
    ) {
      return
    }

    setSection(nextSection)
    setCreators([])
    setHasUnsavedChanges(false)
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return

    const oldIndex = creators.findIndex((creator) => creator._id === active.id)
    const newIndex = creators.findIndex((creator) => creator._id === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    setCreators(moveElement([...creators], oldIndex, newIndex))
    setHasUnsavedChanges(true)
  }

  const saveOrder = async () => {
    try {
      await updateCreatorOrder({
        section,
        creatorIds: creators.map((creator) => creator._id),
      }).unwrap()

      setCreators((currentCreators) =>
        currentCreators.map((creator, position) => ({
          ...creator,
          position,
        })),
      )
      setHasUnsavedChanges(false)
    } catch {
      // The API mutation displays the backend error and keeps the local order.
    }
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <PageHeader title="Arrange creators" className="!mb-2" />
            <p className="max-w-2xl text-sm text-sec-black">
              Drag creators into the order customers should see in each section.
              Creators without a saved order appear after curated creators until
              you save this list.
            </p>
          </div>

          <Button
            label="Save order"
            primary
            size="medium"
            loading={isSaving}
            disabled={!hasUnsavedChanges || isSaving || creators.length === 0}
            onClick={saveOrder}
          />
        </div>

        <TabNav
          activeTab={section}
          setActiveTab={(tab) => changeSection(tab as CreatorSection)}
          tabs={sectionTabs}
          type="switcher"
          className="max-w-xl"
        />

        <div className="rounded-xl border border-divider-gray bg-white p-4 md:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">
                {sectionTabs.find((tab) => tab.id === section)?.name} creators
              </h2>
              <p className="text-sm text-sec-black">
                {creators.length} creator{creators.length === 1 ? '' : 's'} in
                this section
              </p>
            </div>

            <Button
              label="Reset changes"
              size="small"
              variant="border-grey"
              disabled={!hasUnsavedChanges || isSaving}
              onClick={() => {
                setCreators(data?.data.creators ?? [])
                setHasUnsavedChanges(false)
              }}
            />
          </div>

          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={creators.map((creator) => creator._id)}
              strategy={verticalListSortingStrategy}
            >
              <TableComponent
                headers={['Position', 'Creator', 'Status']}
                rows={creators.map((creator, index) => ({
                  id: creator._id,
                  content: [
                    index + 1,
                    <div
                      className="flex items-center gap-3"
                      key={`${creator._id}-name`}
                    >
                      {creator.logo ? (
                        <VendorImage img={creator.logo} />
                      ) : (
                        <Avatar
                          name={creator.businessName || 'Creator'}
                          useRandomBgColor
                        />
                      )}
                      <span>{creator.businessName || 'Unnamed creator'}</span>
                    </div>,
                    <Status
                      key={`${creator._id}-status`}
                      text={creator.status}
                      type={creator.status === 'active' ? 'success' : 'fail'}
                    />,
                  ],
                }))}
                name={`${section.toLowerCase()}-creator-order`}
                loading={isFetching}
                isEmpty={!isFetching && creators.length === 0}
                emptyText="No creators belong to this section yet"
                sortable
              />
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </Layout>
  )
}
